import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState, type DragEvent } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Paper from "@mui/material/Paper";
import LinearProgress from "@mui/material/LinearProgress";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import LinkIcon from "@mui/icons-material/Link";
import WorkOutlineIcon from "@mui/icons-material/WorkOutlined";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { useAutoTour } from "@/components/walkthrough/useAutoTour";
import { JOBS_TOUR } from "@/components/walkthrough/tours";
import { useAuth } from "@/hooks/useAuth";
import {
  jdsQueryKey,
  fetchJDsWithScores,
  createJD,
  deleteJD,
  type JDWithScore,
} from "@/lib/queries/jds";
import { fetchJdFromUrl } from "@/server/fetch-jd-from-url";
import { normalizeFetchedJd } from "@/lib/normalizeFetchedJd";
import { getServerErrorMessage } from "@/lib/serverErrors";
import { scoreMatch } from "@/server/score-match";
import { recordJdSave } from "@/server/record-jd-save";
import { supabase } from "@/integrations/supabase/client";
import { useQuotaError } from "@/hooks/useQuotaError";
import { CAPABILITIES } from "@/lib/quotas";
import { QuotaUpgradeDialog } from "@/components/QuotaUpgradeDialog";
import { useApplications, type ApplicationStatus } from "@/context/ApplicationContext";
import { matchScoreQueryKey, type StoredMatch } from "@/lib/queries/matchScores";
import { jobSummaryQueryKey } from "@/lib/queries/jobSummary";
import {
  STAGE_KEYS,
  STAGE_LABELS,
  daysBetween,
  stageDisplay,
  type StageKey,
} from "@/lib/stages";
import type { ApplicationStage } from "@/lib/queries/applicationStages";


export const Route = createFileRoute("/app/jobs/")({
  component: JobsPage,
  head: () => ({ meta: [{ title: "Jobs — JobYoda" }] }),
});

// Board columns mix non-stage statuses with stage keys.
type ColumnKey = "Saved" | "Applied" | StageKey | "Rejected";

const KANBAN_COLUMNS: ColumnKey[] = [
  "Saved",
  "Applied",
  ...STAGE_KEYS, // recruiter, hiring_manager, portfolio, panel, leadership, offer
  "Rejected",
];

const COLUMN_LABEL: Record<ColumnKey, string> = {
  Saved: "Saved",
  Applied: "Applied",
  recruiter: STAGE_LABELS.recruiter,
  hiring_manager: STAGE_LABELS.hiring_manager,
  portfolio: STAGE_LABELS.portfolio,
  panel: STAGE_LABELS.panel,
  leadership: STAGE_LABELS.leadership,
  offer: STAGE_LABELS.offer,
  Rejected: "Rejected",
};

const COLUMN_COLOR: Record<ColumnKey, "default" | "primary" | "info" | "warning" | "success" | "error" | "secondary"> = {
  Saved: "default",
  Applied: "info",
  recruiter: "primary",
  hiring_manager: "primary",
  portfolio: "secondary",
  panel: "secondary",
  leadership: "warning",
  offer: "success",
  Rejected: "error",
};

const STATUS_COLORS: Record<ApplicationStatus | "Saved", "default" | "primary" | "info" | "warning" | "success" | "error" | "secondary"> = {
  Saved: "default",
  Applied: "info",
  Interview: "warning",
  Offer: "success",
  Rejected: "error",
  Withdrawn: "secondary",
};

type JobRow = JDWithScore & {
  status: ApplicationStatus | "Saved";
  appliedAt: string | null;
  applicationId: string | null;
  currentStage: ApplicationStage | null;
  column: ColumnKey;
};

function relativeDate(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function scoreColor(score: number | null): "success" | "primary" | "warning" | "error" | "default" {
  if (score == null) return "default";
  if (score >= 8) return "success";
  if (score >= 6) return "primary";
  if (score >= 4) return "warning";
  return "error";
}

function resolveColumn(
  status: ApplicationStatus | "Saved",
  currentStage: ApplicationStage | null,
): ColumnKey {
  if (status === "Saved" || status === "Applied" || status === "Rejected") return status;
  if (status === "Withdrawn") return "Rejected";
  if (status === "Offer") return "offer";
  // Interview
  if (currentStage) return currentStage.stage_key as ColumnKey;
  return "hiring_manager";
}

function JobsPage() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  useAutoTour(JOBS_TOUR, "/app/jobs");
  const {
    applications,
    add: addApplication,
    update: updateApplication,
    addStage,
    advanceToStage,
    updateStagePatch,
    currentStageFor,
  } = useApplications();

  const [search, setSearch] = useState("");
  const [view, setView] = useState<"list" | "board" | "funnel">("board");
  const [addOpen, setAddOpen] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<ColumnKey | null>(null);
  const [funnelFilter, setFunnelFilter] = useState<ColumnKey | null>(null);


  const { data: jds, isLoading } = useQuery({
    queryKey: userId ? jdsQueryKey(userId) : ["jds", "anon"],
    queryFn: () => fetchJDsWithScores(userId as string),
    enabled: !!userId,
  });

  const rows: JobRow[] = useMemo(() => {
    const list = jds ?? [];
    return list.map((j) => {
      const app = applications.find((a) => a.jdId === j.id);
      const status = (app?.status ?? "Saved") as ApplicationStatus | "Saved";
      const currentStage = app ? currentStageFor(app.id) : null;
      return {
        ...j,
        status,
        appliedAt: app?.appliedAt ?? null,
        applicationId: app?.id ?? null,
        currentStage,
        column: resolveColumn(status, currentStage),
      };
    });
  }, [jds, applications, currentStageFor]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = rows;
    if (q) {
      base = base.filter(
        (j) =>
          (j.role_title ?? "").toLowerCase().includes(q) ||
          (j.company ?? "").toLowerCase().includes(q) ||
          (j.tags ?? []).some((t) => (t ?? "").toLowerCase().includes(q)) ||
          (j.jd_text ?? "").toLowerCase().includes(q),
      );
    }
    if (funnelFilter && view === "list") {
      base = base.filter((j) => j.column === funnelFilter);
    }
    return base;
  }, [rows, search, funnelFilter, view]);

  const removeMutation = useMutation({
    mutationFn: (id: string) => deleteJD(userId as string, id),
    onSuccess: () => {
      if (userId) queryClient.invalidateQueries({ queryKey: jdsQueryKey(userId) });
      toast.success("Job removed.");
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Failed to delete."),
  });


  const handleRowClick = (id: string) => navigate({ to: "/app/jobs/$jobId", params: { jobId: id } });

  // Kanban DnD
  const handleDragStart = (e: DragEvent<HTMLDivElement>, id: string) => {
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
    setDraggingId(id);
  };
  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverColumn(null);
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>, col: ColumnKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverColumn !== col) setDragOverColumn(col);
  };

  const moveJobToColumn = async (job: JobRow, col: ColumnKey) => {
    if (job.column === col) return;
    if (col === "Saved") {
      toast.info("To stop tracking, delete from the detail page.");
      return;
    }

    try {
      // Stage column → ensure application exists, status = Interview, advance/add stage
      if ((STAGE_KEYS as string[]).includes(col)) {
        const stageKey = col as StageKey;
        let applicationId = job.applicationId;
        if (!applicationId) {
          applicationId = crypto.randomUUID();
          addApplication({
            id: applicationId,
            company: job.company || "—",
            role: job.role_title || "—",
            status: "Interview",
            matchScore: job.lastScore,
            rejectionReason: null,
            appliedAt: new Date().toISOString().slice(0, 10),
            jdId: job.id,
          });
          await addStage(applicationId, stageKey);
        } else {
          if (job.status !== "Interview" && stageKey !== "offer") {
            updateApplication(applicationId, { status: "Interview" });
          }
          if (job.currentStage && job.currentStage.stage_key !== stageKey) {
            await advanceToStage(applicationId, stageKey);
          } else if (!job.currentStage) {
            await addStage(applicationId, stageKey);
          }
        }
        toast.success(`Moved to ${COLUMN_LABEL[col]}.`);
        return;
      }

      // Non-stage columns: Applied / Rejected
      if (col === "Applied") {
        if (job.applicationId) {
          updateApplication(job.applicationId, { status: "Applied" });
        } else {
          addApplication({
            id: crypto.randomUUID(),
            company: job.company || "—",
            role: job.role_title || "—",
            status: "Applied",
            matchScore: job.lastScore,
            rejectionReason: null,
            appliedAt: new Date().toISOString().slice(0, 10),
            jdId: job.id,
          });
        }
        toast.success("Moved to Applied.");
      } else if (col === "Rejected") {
        if (job.applicationId) {
          updateApplication(job.applicationId, { status: "Rejected" });
          if (job.currentStage && job.currentStage.outcome === "pending") {
            await updateStagePatch(job.currentStage.id, { outcome: "rejected" });
          }
        } else {
          addApplication({
            id: crypto.randomUUID(),
            company: job.company || "—",
            role: job.role_title || "—",
            status: "Rejected",
            matchScore: job.lastScore,
            rejectionReason: null,
            appliedAt: new Date().toISOString().slice(0, 10),
            jdId: job.id,
          });
        }
        toast.success("Moved to Rejected.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Move failed.");
    }
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>, col: ColumnKey) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    handleDragEnd();
    if (!id) return;
    const job = rows.find((r) => r.id === id);
    if (!job) return;
    await moveJobToColumn(job, col);
  };

  // Funnel counts
  const funnelCounts = useMemo(() => {
    const counts: Record<ColumnKey, number> = {
      Saved: 0,
      Applied: 0,
      recruiter: 0,
      hiring_manager: 0,
      portfolio: 0,
      panel: 0,
      leadership: 0,
      offer: 0,
      Rejected: 0,
    };
    for (const r of rows) counts[r.column] = (counts[r.column] ?? 0) + 1;
    return counts;
  }, [rows]);

  const funnelMax = Math.max(1, ...Object.values(funnelCounts));

  // Per-stage conversion: passed / (passed + rejected + ghosted) for that stage_key across all stages
  const stageConversion = useMemo(() => {
    const tally: Record<string, { passed: number; closed: number }> = {};
    for (const app of applications) {
      const all = currentStageFor(app.id) ? null : null; // satisfy lint
    }
    // Walk every stage
    for (const r of rows) {
      // r.currentStage already represents one; we need full list. Pull via context separately.
    }
    return tally;
  }, [applications, rows, currentStageFor]);
  void stageConversion;

  return (
    <Box>
      <PageHeader
        title="Jobs"
        subtitle="Every saved JD and tracked application — in one place."
        actions={
          <Button data-tour="jobs-add" variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add job
          </Button>
        }
      />

      <Stack spacing={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            data-tour="jobs-search"
            placeholder="Search role, company, tag, or text…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <ToggleButtonGroup
            data-tour="jobs-views"
            value={view}
            exclusive
            size="small"
            onChange={(_, v) => v && setView(v)}
            aria-label="View mode"
          >
            <ToggleButton value="board" aria-label="Kanban view">
              <ViewKanbanIcon fontSize="small" sx={{ mr: 0.5 }} /> Kanban
            </ToggleButton>
            <ToggleButton value="list" aria-label="List view">
              <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} /> List
            </ToggleButton>
            <ToggleButton value="funnel" aria-label="Funnel view">
              <FilterAltIcon fontSize="small" sx={{ mr: 0.5 }} /> Funnel
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {funnelFilter && view === "list" && (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Chip
              size="small"
              color={COLUMN_COLOR[funnelFilter]}
              label={`Filtered: ${COLUMN_LABEL[funnelFilter]}`}
              onDelete={() => setFunnelFilter(null)}
            />
          </Stack>
        )}


        {isLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Card>
            <CardContent>
              <Stack spacing={2} sx={{ alignItems: "center", py: 6 }}>
                <WorkOutlineIcon sx={{ fontSize: 56, color: "text.disabled" }} />
                <Typography variant="h6">No jobs yet</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", maxWidth: 480 }}>
                  Add a job by URL or paste the description. Each job becomes a workspace with success scoring,
                  Q&A, materials, and interview prep.
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                  Add your first job
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ) : view === "list" ? (
          filtered.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                  No jobs match these filters.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {filtered.map((j) => (
                <JobListCard
                  key={j.id}
                  job={j}
                  onOpen={() => handleRowClick(j.id)}
                  onDelete={() => removeMutation.mutate(j.id)}
                  onMove={(col) => moveJobToColumn(j, col)}
                />
              ))}
            </Stack>
          )
        ) : view === "board" ? (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              pb: 1,
              // give scrollbar some room
              mx: -1,
              px: 1,
            }}
          >
            {KANBAN_COLUMNS.map((col) => {
              const items = filtered.filter((r) => r.column === col);
              const isOver = dragOverColumn === col;
              return (
                <Paper
                  key={col}
                  elevation={1}
                  onDragOver={(e) => handleDragOver(e, col)}
                  onDragLeave={() => setDragOverColumn(null)}
                  onDrop={(e) => handleDrop(e, col)}
                  sx={{
                    p: 1.5,
                    minWidth: 240,
                    maxWidth: 240,
                    minHeight: 480,
                    flexShrink: 0,
                    bgcolor: isOver ? "action.hover" : "background.default",
                    border: isOver ? "2px dashed" : "2px solid transparent",
                    borderColor: isOver ? `${COLUMN_COLOR[col]}.main` : "transparent",
                  }}
                >
                  <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                    <Chip size="small" color={COLUMN_COLOR[col]} label={COLUMN_LABEL[col]} />
                    <Chip size="small" label={items.length} />
                  </Stack>
                  <Stack spacing={1.25}>
                    {items.length === 0 ? (
                      <Box
                        sx={{
                          py: 3,
                          textAlign: "center",
                          color: "text.disabled",
                          border: "1px dashed",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption">Drop here</Typography>
                      </Box>
                    ) : (
                      items.map((j) => (
                        <KanbanJobCard
                          key={j.id}
                          job={j}
                          isDragging={draggingId === j.id}
                          onDragStart={(e) => handleDragStart(e, j.id)}
                          onDragEnd={handleDragEnd}
                          onOpen={() => handleRowClick(j.id)}
                          onDelete={() => removeMutation.mutate(j.id)}
                          onMove={(col) => moveJobToColumn(j, col)}
                        />
                      ))
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        ) : (
          // Funnel view
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Pipeline funnel
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click a stage to filter the list view. Conversion rate = next-stage count ÷ this-stage count, including everything that passed through.
              </Typography>
              <Stack spacing={1}>
                {KANBAN_COLUMNS.map((col, i) => {
                  const count = funnelCounts[col];
                  const next = KANBAN_COLUMNS[i + 1];
                  const nextCount = next ? funnelCounts[next] : null;
                  const conv =
                    nextCount != null && count > 0
                      ? Math.round((nextCount / count) * 100)
                      : null;
                  const pct = (count / funnelMax) * 100;
                  return (
                    <Box key={col}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                        <Typography variant="caption" sx={{ width: 120, fontWeight: 600 }}>
                          {COLUMN_LABEL[col]}
                        </Typography>
                        <Box
                          onClick={() => {
                            if (count === 0) return;
                            setFunnelFilter(col);
                            setView("list");
                          }}
                          sx={{
                            flex: 1,
                            cursor: count > 0 ? "pointer" : "default",
                            "&:hover": count > 0 ? { opacity: 0.85 } : undefined,
                          }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            color={COLUMN_COLOR[col] === "default" ? "primary" : (COLUMN_COLOR[col] as any)}
                            sx={{ height: 18, borderRadius: 1 }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ width: 36, textAlign: "right", fontWeight: 600 }}>
                          {count}
                        </Typography>
                      </Stack>
                      {conv != null && next && (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: "128px" }}>
                          → {COLUMN_LABEL[next]}: {conv}% conversion
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>


      <AddJobDialog
        open={addOpen}
        userId={userId}
        onClose={() => setAddOpen(false)}
        onCreated={(id) => {
          if (userId) queryClient.invalidateQueries({ queryKey: jdsQueryKey(userId) });
          setAddOpen(false);
          navigate({ to: "/app/jobs/$jobId", params: { jobId: id } });
        }}
      />
    </Box>
  );
}

function JobListCard({
  job,
  onOpen,
  onDelete,
  onMove,
}: {
  job: JobRow;
  onOpen: () => void;
  onDelete: () => void;
  onMove: (col: ColumnKey) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <Card
      variant="outlined"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={`Open ${job.role_title || "job"} workspace`}
      sx={{
        cursor: "pointer",
        transition: "box-shadow 120ms, border-color 120ms, background-color 120ms",
        "&:hover": { boxShadow: 3, borderColor: "primary.light" },
      }}
    >
      <CardContent sx={{ pb: 1.5 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <JobCardBody job={job} />
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
            aria-label="more"
          >
            <MoreVertIcon />
          </IconButton>
          <MoveMenu
            anchorEl={anchorEl}
            currentColumn={job.column}
            onClose={() => setAnchorEl(null)}
            onMove={onMove}
            onDelete={onDelete}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function JobCardBody({ job }: { job: JobRow }) {
  const stageDays = job.currentStage ? daysBetween(job.currentStage.entered_at, job.currentStage.outcome_at) : 0;
  const ghostRisk = job.currentStage?.outcome === "pending" && stageDays > 7;
  const ghostHigh = job.currentStage?.outcome === "pending" && stageDays > 14;
  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5, flexWrap: "wrap" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {job.role_title || "Untitled role"}
        </Typography>
        {job.company && (
          <Typography variant="body2" color="text.secondary">
            · {job.company}
          </Typography>
        )}
        <Chip
          size="small"
          color={STATUS_COLORS[job.status]}
          label={
            job.status === "Interview" && job.currentStage
              ? stageDisplay(job.currentStage)
              : job.status
          }
          sx={{ fontWeight: 600 }}
        />
        {job.currentStage?.outcome === "pending" && stageDays > 0 && (
          <Chip
            size="small"
            variant="outlined"
            color={ghostHigh ? "error" : ghostRisk ? "warning" : "default"}
            label={`Day ${stageDays}`}
            sx={{ height: 20, fontSize: "0.7rem" }}
          />
        )}
        {job.lastScore != null && (
          <Tooltip title={`Last scored ${relativeDate(job.lastScoredAt as string)}`}>
            <Chip
              size="small"
              color={scoreColor(job.lastScore)}
              label={`Success ${job.lastScore.toFixed(1)}`}
              sx={{ fontWeight: 600 }}
            />
          </Tooltip>
        )}
        {job.hasCachedQA && (
          <Tooltip title="Q&A questions generated and cached for this job">
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              icon={<QuestionAnswerOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Q&A ready"
            />
          </Tooltip>
        )}
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          mb: 1,
        }}
      >
        {job.jd_text}
      </Typography>
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }} useFlexGap>
        {(job.tags ?? []).map((t) => (
          <Chip key={t} size="small" variant="outlined" label={t} />
        ))}
        <Chip size="small" variant="outlined" label={`Saved ${relativeDate(job.created_at)}`} />
      </Stack>
    </>
  );
}

function KanbanJobCard({
  job,
  isDragging,
  onDragStart,
  onDragEnd,
  onOpen,
  onDelete,
  onMove,
}: {
  job: JobRow;
  isDragging: boolean;
  onDragStart: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onOpen: () => void;
  onDelete: () => void;
  onMove: (col: ColumnKey) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const stageDays = job.currentStage ? daysBetween(job.currentStage.entered_at, job.currentStage.outcome_at) : 0;
  const ghostRisk = job.currentStage?.outcome === "pending" && stageDays > 7;
  const ghostHigh = job.currentStage?.outcome === "pending" && stageDays > 14;
  return (
    <Card
      variant="outlined"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      sx={{
        cursor: "grab",
        opacity: isDragging ? 0.4 : 1,
        "&:hover": { boxShadow: 2 },
        "&:active": { cursor: "grabbing" },
      }}
    >
      <CardContent sx={{ pb: "12px !important" }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
              {job.role_title || "Untitled role"}
            </Typography>
            {job.company && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {job.company}
              </Typography>
            )}
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
              {job.lastScore != null && (
                <Chip
                  size="small"
                  label={job.lastScore.toFixed(1)}
                  color={scoreColor(job.lastScore)}
                  sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                />
              )}
              {job.currentStage?.outcome === "pending" && stageDays > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={ghostHigh ? "error" : ghostRisk ? "warning" : "default"}
                  label={`Day ${stageDays}`}
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
              {job.appliedAt && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={relativeDate(job.appliedAt)}
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
            </Stack>
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
            aria-label="more"
            sx={{ mt: -0.5, mr: -0.5 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <MoveMenu
            anchorEl={anchorEl}
            currentColumn={job.column}
            onClose={() => setAnchorEl(null)}
            onMove={onMove}
            onDelete={onDelete}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function MoveMenu({
  anchorEl,
  currentColumn,
  onClose,
  onMove,
  onDelete,
}: {
  anchorEl: HTMLElement | null;
  currentColumn: ColumnKey;
  onClose: () => void;
  onMove: (col: ColumnKey) => void;
  onDelete: () => void;
}) {
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
    >
      <MenuItem disabled dense sx={{ opacity: 0.7, fontSize: "0.75rem" }}>
        Move to…
      </MenuItem>
      {KANBAN_COLUMNS.filter((c) => c !== currentColumn && c !== "Saved").map((col) => (
        <MenuItem
          key={col}
          onClick={(e) => {
            e.stopPropagation();
            onMove(col);
            onClose();
          }}
          dense
        >
          <Chip
            size="small"
            color={COLUMN_COLOR[col]}
            label={COLUMN_LABEL[col]}
            sx={{ mr: 1, height: 20, fontSize: "0.7rem" }}
          />
        </MenuItem>
      ))}
      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
          onClose();
        }}
        sx={{ color: "error.main", borderTop: "1px solid", borderColor: "divider", mt: 0.5 }}
        dense
      >
        <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
      </MenuItem>
    </Menu>
  );
}

function AddJobDialog({
  open,
  userId,
  onClose,
  onCreated,
}: {
  open: boolean;
  userId: string | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const quotaDialog = useQuotaError(CAPABILITIES.jd_save);
  const [url, setUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [roleTitle, setRoleTitle] = useState("");
  const [company, setCompany] = useState("");
  const [jdText, setJdText] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [saving, setSaving] = useState(false);
  const [extractedMeta, setExtractedMeta] = useState<{
    location: string | null;
    work_arrangement: string | null;
    salary_min: number | null;
    salary_max: number | null;
    salary_currency: string | null;
    salary_period: string | null;
  } | null>(null);

  const handleFetchUrl = async () => {
    if (!url.trim()) return;
    setFetching(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new Error("You must be signed in.");
      const raw = await fetchJdFromUrl({
        data: { url: url.trim() },
        headers: { authorization: `Bearer ${token}` },
      });
      const res = normalizeFetchedJd(raw);
      if (!res.jd_text) {
        throw new Error("The server returned no job description text. Paste it instead.");
      }
      setJdText(res.jd_text);
      if (res.role_title && !roleTitle) setRoleTitle(res.role_title);
      if (res.company && !company) setCompany(res.company);
      setExtractedMeta({
        location: res.location,
        work_arrangement: res.work_arrangement,
        salary_min: res.salary_min,
        salary_max: res.salary_max,
        salary_currency: res.salary_currency,
        salary_period: res.salary_period,
      });
      const chars = res.jd_text.length;
      const label = [res.role_title, res.company].filter(Boolean).join(" · ");
      toast.success(`JD fetched${label ? `: ${label}` : ""} (${chars.toLocaleString()} chars).`);
    } catch (e) {
      toast.error(await getServerErrorMessage(e, "Failed to fetch JD."));
    } finally {
      setFetching(false);
    }
  };

  const canSave = (jdText ?? "").trim().length >= 20 && !!userId && !saving;

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      // Enforce + count the JD-save quota first.
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new Error("You must be signed in.");
      try {
        await recordJdSave({ headers: { authorization: `Bearer ${token}` } });
      } catch (e) {
        if (quotaDialog.handle(e)) {
          setSaving(false);
          return;
        }
        throw e;
      }

      const tags = (tagsText ?? "").split(",").map((t) => t.trim()).filter(Boolean);
      const id = await createJD(userId, {
        jd_text: (jdText ?? "").trim(),
        role_title: (roleTitle ?? "").trim() || null,
        company: (company ?? "").trim() || null,
        tags,
        notes: null,
        ...(extractedMeta ?? {}),
      });
      const jdTextSnapshot = (jdText ?? "").trim();
      toast.success("Job created. Calculating success score…");
      // reset
      setUrl("");
      setJdText("");
      setRoleTitle("");
      setCompany("");
      setTagsText("");
      onCreated(id);

      // Fire-and-forget: auto-calculate the success score in the background.
      void (async () => {
        try {
          const res = await scoreMatch({
            data: { jdText: jdTextSnapshot, jdId: id },
            headers: { authorization: `Bearer ${token}` },
          });
          const stored: StoredMatch = {
            score: res.score,
            verdict: res.verdict,
            dimensions: res.dimensions,
            strengths: res.strengths,
            gaps: res.gaps,
            scoredAt: new Date().toISOString(),
          };
          queryClient.setQueryData(matchScoreQueryKey(userId, id), stored);
          queryClient.invalidateQueries({ queryKey: jdsQueryKey(userId) });
          queryClient.invalidateQueries({ queryKey: jobSummaryQueryKey(userId, id) });
          toast.success(`Success score: ${res.score.toFixed(1)} / 10`);
        } catch (e) {
          // Quota-exceeded on auto-score is benign here — user can re-run from match tab.
          if (quotaDialog.handle(e)) return;
          toast.error(
            e instanceof Error ? `Auto-scoring failed: ${e.message}` : "Auto-scoring failed.",
          );
        }
      })();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Add a job</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              label="Fetch JD from URL"
              placeholder="https://… (any job posting site)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="outlined"
              onClick={handleFetchUrl}
              disabled={fetching || !url.trim()}
              startIcon={fetching ? <CircularProgress size={14} /> : null}
              sx={{ whiteSpace: "nowrap" }}
            >
              {fetching ? "Fetching…" : "Fetch"}
            </Button>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Role title" value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} fullWidth />
            <TextField label="Company" value={company} onChange={(e) => setCompany(e.target.value)} fullWidth />
          </Stack>
          <TextField
            label="Tags (comma separated)"
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            placeholder="frontend, remote, dream-co"
            fullWidth
          />
          <TextField
            label="Job description"
            value={jdText}
            onChange={(e) => setJdText(e.target.value)}
            multiline
            minRows={8}
            required
            helperText="Paste the JD or fetch it from a URL. Minimum 20 characters."
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {saving ? "Saving…" : "Add job"}
        </Button>
      </DialogActions>
      <QuotaUpgradeDialog
        open={quotaDialog.open}
        onClose={quotaDialog.close}
        quota={quotaDialog.quota}
        message={quotaDialog.message}
      />
    </Dialog>
  );
}
